import React, { useState, useEffect, FormEvent, useCallback, useMemo } from 'react';
import AllVehicleTable from './Table/AllVehicleTable';
import Pagination from 'reusable/pagination';
import axiosInstance from 'config/axiosInstance';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
} from 'reusable/Dialog/dialogInterface';
import {
  addVehicleFields,
  allVehicleSortingOptions,
  deleteVehicleFields,
  editVehicleFields,
} from './AllVehicleFields';
import EditDialog from 'reusable/Dialog/editDialog';
import AddDialog from 'reusable/Dialog/addDialog';
import AddButton from 'reusable/addButton';
import DeleteDialog from 'reusable/Dialog/deleteDialog';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import Export from 'reusable/export';
import { CsvCondition, csvDateTime } from 'config/csv';
import { useTranslation } from 'react-i18next';
import ErrorPopup from 'reusable/ErrorPopup';
import { classifiedErrorMessages } from 'config/apiErrors';
import { isUnresolvedCreate, submitIdempotentCreate } from 'config/idempotentCreate';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { pageAfterRemoval, pageToRecover } from 'reusable/pageRecovery';
import { PageSizeSelect, usePageSize } from 'reusable/PageSize';
import { vehicleRecordsExport } from 'reusable/DeleteExport';
import { fetchCarModelCandidates } from './carModelCandidates';

const AllVehicleManagement: React.FC = () => {
  const { t, i18n } = useTranslation(['vehicle', 'common']);
  // 保存・削除に失敗したときの説明。以前はコンソールに出すだけだった。
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const showSaveError = (error: unknown) =>
    setErrorMessages(
      classifiedErrorMessages(error, {
        network: t('ui.error-network', { ns: 'common' }),
        forbidden: t('ui.error-forbidden', { ns: 'common' }),
        server: t('ui.error-server', { ns: 'common' }),
        fallback: t('ui.save-failed', { ns: 'common' }),
      })
    );
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  // EV-493。1ページの件数（20・50・100、初めは 50。localStorage に覚える）。以前は 10件で固定。
  const [rowsPerPage, setRowsPerPage] = usePageSize('vehicleList.pageSize');
  const [vehicleData, setVehicleData] = useState<any[]>([]);
  // EV-142。次の検索条件ではなく、今の行が取得できた時点の条件を出力する。
  const [exportConditions, setExportConditions] = useState<CsvCondition[]>([['page', 1]]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    note: '',
    fields: [],
  });
  const [currentVehicleId, setCurrentVehicleId] = useState<string | null>(null);
  // A07（2026-11-03 和田様の UAT）。車種の入力の候補（同じ会社で過去に入れた車種）。追加・編集を初めて開いたときに読む。
  const [carModels, setCarModels] = useState<string[] | null>(null);
  const loadCarModels = () => {
    if (carModels !== null) return;
    setCarModels([]);
    fetchCarModelCandidates().then(setCarModels);
  };
  // 欄の配列は、候補が変わったときだけ作り直す（ダイアログは欄が変わると入力を引き継いで初期化し直す）。
  const dialogFields = useMemo(
    () =>
      dialogProps.fields.map((field) =>
        field.name === 'car_model' && field.type === 'text' && carModels && carModels.length > 0
          ? { ...field, suggestions: carModels }
          : field
      ),
    [dialogProps.fields, carModels]
  );
  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchProfilePicture = async (driverId: string) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${driverId}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(
        `Failed to fetch profile picture for driver ${driverId}`,
        error
      );
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      let apiUrl = `/v1/vehicles/?order_by=${sortValue}&page=${currentPage}&page_size=${rowsPerPage}&search=${encodeURIComponent(searchValue)}`;
      const response = await axiosInstance.get(apiUrl);
      const fetchedVehicleData = response.data.results;
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, rowsPerPage, {
        count: response.data.count,
        rows: fetchedVehicleData.length,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }

      const updatedVehicleData = await Promise.all(
        fetchedVehicleData.map(async (vehicle: any) => {
          if (vehicle.last_used_by?.id) {
            const profilePicture = await fetchProfilePicture(
              vehicle.last_used_by.id
            );
            return {
              ...vehicle,
              last_used_by: {
                ...vehicle.last_used_by,
                profile_picture: profilePicture,
              },
            };
          }
          return vehicle;
        })
      );

      if (!isCurrent()) return;
      setVehicleData(updatedVehicleData);
      setExportConditions([['search', searchValue], ['sort', sortValue], ['page', currentPage]]);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recover = pageToRecover(currentPage, rowsPerPage, { error });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      console.error('Failed to fetch data:', error);
      setFetchError(error);
    }
  }, [searchValue, sortValue, currentPage, rowsPerPage, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDialog = (dialogConfig: DialogConfig, dialogType: DialogType) => {
    setDialogProps(dialogConfig);
    if (dialogType === DialogType.Add) setIsAddDialogOpen(true);
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
    if (dialogType === DialogType.Delete) setIsDeleteDialogOpen(true);
  };

  const closeDialog = () => {
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setCurrentVehicleId(null);
  };

  const handleDialog = async (dialogType: DialogType, id?: string) => {
    if (dialogType === DialogType.Add || dialogType === DialogType.Edit) loadCarModels();
    if (dialogType === DialogType.Add) {
      openDialog(
        {
          title: `${t('all-vehicle.add-vehicle.title')}`,
          fields: addVehicleFields(t),
        },
        DialogType.Add
      );
    } else if (
      (dialogType === DialogType.Edit || dialogType === DialogType.Delete) &&
      id
    ) {
      try {
        const vehicleResponse = await axiosInstance.get(`/v1/vehicles/${id}/`);
        const vehicleData = vehicleResponse.data;
        setCurrentVehicleId(id);
        if (dialogType === DialogType.Edit) {
          openDialog(
            {
              title: `${t('all-vehicle.edit-vehicle.title')}`,
              fields: editVehicleFields(t, vehicleData),
            },
            DialogType.Edit
          );
        } else if (dialogType === DialogType.Delete) {
          openDialog(
            {
              title: `${t('all-vehicle.delete-vehicle.title')}`,
              subtitle: `${t('all-vehicle.delete-vehicle.info')}`,
              note: `${t('all-vehicle.delete-vehicle.info2')}`,
              fields: deleteVehicleFields(t, vehicleData),
            },
            DialogType.Delete
          );
        }
      } catch (error) {
        console.error('Failed to fetch vehicle data', error);
        // ADM-15。開けなかった理由を出す（以前は押しても何も起きないように見えた）。
        showSaveError(error);
      }
    }
  };

  const handleAddVehicle = async (
    e: FormEvent<Element>,
    newData: NewDataField
  ) => {
    e.preventDefault();

    // EV-131（ADM-14 の AC1）。作成の操作ごとに X-Request-ID を作り、結果が分からない失敗では
    // 同じ値・同じ本文で送り直す。結果が分からなかった後に入力を変えずに押したら前回と同じ値
    // （「この車両番号はすでにある」ではなく前回の結果が返る）。24時間の期限・前回の値を残す
    // 条件・タブに残す・利用者の印は配送先の作成と共通（config/idempotentCreate.ts）。送信中の
    // 入力・閉じるの停止は AddDialog。
    const outcome = await submitIdempotentCreate('vehicle', '/v1/vehicles/', { ...newData });
    if (outcome.status === 'created') {
      setIsAddDialogOpen(false);
      setVehicleData((prevData) => [...prevData, outcome.response.data]);
      window.location.reload();
    } else if (outcome.status === 'expired') {
      // RV-127。期限が近く、前回の値は使えない。一覧を読み直して警告する（次の押下は新しい値）。
      await fetchData();
      setErrorMessages([t('all-vehicle.add-vehicle.create-retry-expired')]);
    } else if (outcome.status === 'unknown') {
      // 作成できたかを確かめられなかった（値は残した）。ダイアログと入力を残し、一覧を読み直す。
      console.error('Failed to add vehicle:', outcome.error);
      await fetchData();
      setErrorMessages([t('all-vehicle.add-vehicle.create-outcome-unknown')]);
    } else {
      console.error('Failed to add vehicle:', outcome.error);
      // ADM-15。ダイアログを開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(outcome.error);
    }
    // EV-461。結果が分からないまま（前回の X-Request-ID を残した）なら、AddDialog が入力を送った値のまま固定する。
    return { unresolved: isUnresolvedCreate(outcome) };
  };

  const handleEditVehicle = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    try {
      if (updatedData) {
        const response = await axiosInstance.patch(
          `/v1/vehicles/${updatedData.id}/`,
          updatedData
        );

        setVehicleData((prevData) =>
          prevData.map((vehicle) =>
            vehicle.id === updatedData.id ? response.data : vehicle
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      // ADM-15。ダイアログを開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(error);
    }
  };

  const handleDeleteVehicle = async (id: string) => {
    try {
      await axiosInstance.delete(`/v1/vehicles/${id}/`);
      setVehicleData(vehicleData.filter((vehicle) => vehicle.id !== id));
      setIsDeleteDialogOpen(false);
      // EV-460。画面の中で行を除くだけにせず、件数とページを読み直す。今のページの最後の1件なら1つ前のページ。
      const next = pageAfterRemoval(currentPage, vehicleData.length);
      if (next !== currentPage) setCurrentPage(next);
      else await fetchData();
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
      showSaveError(error);
    }
  };

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setRowsPerPage(size);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  return (
    <div>
      {/* EV-309。1024px 未満では、ツールバーの右の組を次の行へ折り返し、右に寄せる。1024px 以上は1行のまま（検索の欄が縮む）。
          右の組も、1024px 未満で収まらないときは中で折り返す。1024px 以上は1段のまま、右の組は縮めず（lg:shrink-0）、検索の欄
          （min-w-0）が縮む（#248 の独立照合の指摘）。 */}
      <div className="flex flex-row max-lg:flex-wrap gap-y-2 justify-between space-y-2">
        <div className="flex w-2/5 min-w-0 justify-start">
          <Search
            placeholder={t('all-vehicle.table.search')}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="ml-auto flex flex-row max-lg:flex-wrap lg:shrink-0 justify-end gap-y-2 space-x-2 items-center">
          <SortBy
            options={allVehicleSortingOptions(t)}
            defaultValue=""
            onChange={handleSortChange}
          />
          <AddButton
            label={t('all-vehicle.table.add-vehicle')}
            onClick={() => handleDialog(DialogType.Add)}
          />
          <AddDialog
            isOpen={isAddDialogOpen}
            onClose={closeDialog}
            handleSubmit={handleAddVehicle}
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('all-vehicle.add-vehicle.save')}
            cancelLabel={t('all-vehicle.add-vehicle.cancel')}
            fields={dialogFields}
          />
          <EditDialog
            isOpen={isEditDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, updatedData: any) =>
              handleEditVehicle(e, {
                ...updatedData,
                id: currentVehicleId ?? '',
              })
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('all-vehicle.edit-vehicle.save')}
            cancelLabel={t('all-vehicle.edit-vehicle.cancel')}
            fields={dialogFields}
            vehicleId={currentVehicleId ?? ''}
          />
          <DeleteDialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any) =>
              handleDeleteVehicle(currentVehicleId || '')
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            note={dialogProps.note ?? ''}
            confirmLabel={t('all-vehicle.delete-vehicle.delete')}
            cancelLabel={t('all-vehicle.delete-vehicle.cancel')}
            fields={dialogProps.fields}
            vehicleId={currentVehicleId ?? ''}
            // UAT A15。その車両の点検の記録を、削除の前に保存できる。
            exportRecords={currentVehicleId ? vehicleRecordsExport(currentVehicleId) : undefined}
          />

          <Export
            data={vehicleData}
            name="vehicles"
            conditions={exportConditions}
            exportFields={[
              { header: 'Vehicle ID', field: 'id' },
              { header: 'Plate Number', field: 'plate_number' },
              { header: 'Car Model', field: 'car_model' },
              { header: 'Last used by', field: 'last_used_by.name' },
              { header: 'Last used by', field: 'last_used_by.phone' },
              { header: 'Added Date', field: 'create_time', customExport: csvDateTime },
              { header: 'Last Inspection', field: 'last_inspection' },
            ]}
          />
        </div>
      </div>

      {fetchError ? (
        <FetchFailed error={fetchError} onRetry={fetchData} />
      ) : (
        <AllVehicleTable
          vehicles={vehicleData}
          onEdit={handleDialog.bind(null, DialogType.Edit)}
          onDelete={handleDialog.bind(null, DialogType.Delete)}
        />
      )}

      <div className="space-y-2">
        <PageSizeSelect id="vehicle-page-size" value={rowsPerPage} onChange={handlePageSizeChange} />
        <Pagination
          allRows={count}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
      {errorMessages.length > 0 && (
        <ErrorPopup
          title={t('ui.error', { ns: 'common' })}
          messages={errorMessages}
          onClose={() => setErrorMessages([])}
        />
      )}
    </div>
  );
};

export default AllVehicleManagement;
